import React, { useEffect, useMemo, useRef, useState } from "react";
import { X, Loader2, RotateCcw, Check, ChevronLeft, ChevronRight, Image as ImageIcon, Eye, Flashlight, FlashlightOff } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { compressImage } from "@/lib/imageCompression";

/**
 * CameraCapture — 1:1 an die native App angelehnt.
 *
 * Aufbau (wie native Kamera):
 *  • Header: X | Kategorie-Titel + Beschreibung | 👁 Beispiel-Overlay-Toggle
 *  • Kategorie-Chips-Reihe über dem Sucher (aktive blau)
 *  • „Wischen für nächstes Bild" — Swipe/Pfeile wechseln die Kategorie
 *  • Ghost-Overlay: Beispielbild halbtransparent über dem Live-Bild (default AN)
 *  • Fortschritts-Punkte pro Kategorie (aktiv = breit, mit Fotos = grün)
 *  • Bottom-Bar: Galerie | Auslöser | Hochladen
 */
export default function CameraCapture({ open, onClose, caseId, categories = [], initialCategory = null, onUploaded, uploadPath = null }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const galleryInputRef = useRef(null);
  const touchX = useRef(null);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState(null);
  const [facingMode, setFacingMode] = useState("environment");
  // Each shot: { blob, dataUrl, category }
  const [shots, setShots] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [activeCategory, setActiveCategory] = useState(initialCategory);
  const [showOverlay, setShowOverlay] = useState(true);
  const [flash, setFlash] = useState(false);
  const chipsRef = useRef(null);
  // ── Zoom (wie native Kamera-App): Hardware-Zoom wenn das Gerät ihn
  //    unterstützt, sonst sanfter Digital-Zoom (max 3×) mit Capture-Crop.
  const [zoom, setZoomState] = useState(1);
  const [hasHwZoom, setHasHwZoom] = useState(false);
  const zoomRef = useRef(1);
  const hwZoomRef = useRef(null); // { track, min, max, step }
  const pinchDist = useRef(null);
  const pinchStartZoom = useRef(1);
  const [zoomBadge, setZoomBadge] = useState(false);
  const zoomBadgeTimer = useRef(null);
  // ── Taschenlampe (Torch) für dunkle Werkstätten — nur wenn das Gerät
  //    sie unterstützt (getCapabilities().torch).
  const [torchOn, setTorchOn] = useState(false);
  const [torchSupported, setTorchSupported] = useState(false);

  useEffect(() => {
    if (!open) return;
    // Start-Kategorie: übergeben oder erste der Liste (wie native App)
    setActiveCategory(initialCategory || categories[0]?.key || null);
    setShowOverlay(true);
  }, [open, initialCategory, categories]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    async function start() {
      setReady(false);
      setError(null);
      try {
        // 4:3 (voller Sensor-Blickwinkel wie die native Kamera-App) statt
        // 16:9 — 16:9 croppt den Sensor und wirkt „herangezoomt".
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode, width: { ideal: 2560 }, height: { ideal: 1920 } },
          audio: false,
        });
        if (cancelled) { stream.getTracks().forEach((t) => t.stop()); return; }
        streamRef.current = stream;
        // Zoom-Setup: Geräte-Zoom auf Minimum (1×) — nie „zu groß" starten.
        const track = stream.getVideoTracks()[0];
        const caps = track?.getCapabilities?.() || {};
        if (caps.zoom && typeof caps.zoom.min === "number") {
          hwZoomRef.current = {
            track,
            min: caps.zoom.min || 1,
            max: Math.min(caps.zoom.max || 8, 8),
            step: caps.zoom.step || 0.1,
          };
          setHasHwZoom(true);
          try { await track.applyConstraints({ advanced: [{ zoom: hwZoomRef.current.min }] }); } catch { /* optional */ }
        } else {
          hwZoomRef.current = null;
          setHasHwZoom(false);
        }
        zoomRef.current = 1;
        setZoomState(1);
        setTorchSupported(!!caps.torch);
        setTorchOn(false);
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play().catch(() => {});
        }
        setReady(true);
      } catch (e) {
        setError(e.message || "Kamera-Zugriff verweigert");
      }
    }
    start();
    return () => {
      cancelled = true;
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
    };
  }, [open, facingMode]);

  const activeIdx = useMemo(
    () => Math.max(0, categories.findIndex((c) => c.key === activeCategory)),
    [categories, activeCategory]
  );
  const activeMeta = categories[activeIdx] || null;

  const gotoCategory = (idx) => {
    if (!categories.length) return;
    const next = ((idx % categories.length) + categories.length) % categories.length;
    setActiveCategory(categories[next].key);
  };

  // Aktiven Chip in Sicht scrollen
  useEffect(() => {
    const el = chipsRef.current?.querySelector('[data-active="true"]');
    el?.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
  }, [activeCategory]);

  // Swipe (1 Finger) wechselt die Kategorie · Pinch (2 Finger) zoomt.
  const applyZoom = (v) => {
    const hw = hwZoomRef.current;
    let next;
    if (hw) {
      // Anzeige-Zoom 1× == Geräte-Minimum; auf echte Constraint-Werte mappen
      const raw = Math.min(hw.max, Math.max(hw.min, v * hw.min));
      next = raw / hw.min;
      zoomRef.current = next;
      setZoomState(next);
      try { hw.track.applyConstraints({ advanced: [{ zoom: raw }] }); } catch { /* optional */ }
    } else {
      next = Math.min(3, Math.max(1, v));
      zoomRef.current = next;
      setZoomState(next);
    }
    setZoomBadge(true);
    clearTimeout(zoomBadgeTimer.current);
    zoomBadgeTimer.current = setTimeout(() => setZoomBadge(false), 1200);
  };

  const onTouchStart = (e) => {
    if (e.touches?.length === 2) {
      touchX.current = null; // Pinch bricht den Kategorie-Swipe ab
      pinchDist.current = Math.hypot(
        e.touches[0].clientX - e.touches[1].clientX,
        e.touches[0].clientY - e.touches[1].clientY
      );
      pinchStartZoom.current = zoomRef.current;
      return;
    }
    touchX.current = e.touches?.[0]?.clientX ?? null;
  };
  const onTouchMove = (e) => {
    if (e.touches?.length === 2 && pinchDist.current) {
      const d = Math.hypot(
        e.touches[0].clientX - e.touches[1].clientX,
        e.touches[0].clientY - e.touches[1].clientY
      );
      // Gedämpfte Übersetzung (×0.6) — feinfühlig statt „zu groß" springen
      const factor = 1 + (d / pinchDist.current - 1) * 0.6;
      applyZoom(pinchStartZoom.current * factor);
    }
  };
  const onTouchEnd = (e) => {
    if (pinchDist.current) {
      if ((e.touches?.length || 0) < 2) pinchDist.current = null;
      return;
    }
    if (touchX.current == null) return;
    const dx = (e.changedTouches?.[0]?.clientX ?? touchX.current) - touchX.current;
    touchX.current = null;
    if (Math.abs(dx) < 55) return;
    gotoCategory(activeIdx + (dx < 0 ? 1 : -1));
  };

  const capture = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas || !ready) return;
    const w = video.videoWidth;
    const h = video.videoHeight;
    // Digital-Zoom: exakt den im Sucher sichtbaren Ausschnitt fotografieren
    // (WYSIWYG). Bei Hardware-Zoom liefert der Stream bereits gezoomte Frames.
    const z = hwZoomRef.current ? 1 : zoomRef.current;
    const sw = Math.round(w / z);
    const sh = Math.round(h / z);
    const sx = Math.round((w - sw) / 2);
    const sy = Math.round((h - sh) / 2);
    canvas.width = sw;
    canvas.height = sh;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(video, sx, sy, sw, sh, 0, 0, sw, sh);
    canvas.toBlob(
      (blob) => {
        if (!blob) return;
        const dataUrl = URL.createObjectURL(blob);
        setShots((prev) => [...prev, { blob, dataUrl, category: activeCategory || "schadenstelle" }]);
        setFlash(true);
        setTimeout(() => setFlash(false), 120);
      },
      "image/jpeg",
      0.9
    );
  };

  const removeShot = (idx) => {
    setShots((prev) => {
      const copy = [...prev];
      const [removed] = copy.splice(idx, 1);
      if (removed?.dataUrl) URL.revokeObjectURL(removed.dataUrl);
      return copy;
    });
  };

  // Fotos aus der Galerie hinzufügen — landen in der aktuell aktiven Kategorie.
  const onGalleryFiles = (fileList) => {
    if (!fileList || fileList.length === 0) return;
    const cat = activeCategory || "schadenstelle";
    Array.from(fileList).forEach((file) => {
      if (!file.type?.startsWith("image/")) return;
      const dataUrl = URL.createObjectURL(file);
      setShots((prev) => [...prev, { blob: file, dataUrl, category: cat }]);
    });
  };

  const flip = () => setFacingMode((m) => (m === "environment" ? "user" : "environment"));

  const toggleTorch = async () => {
    const track = streamRef.current?.getVideoTracks?.()[0];
    if (!track) return;
    try {
      await track.applyConstraints({ advanced: [{ torch: !torchOn }] });
      setTorchOn((v) => !v);
    } catch { /* Gerät ohne steuerbare Taschenlampe */ }
  };

  const close = () => {
    shots.forEach((s) => s.dataUrl && URL.revokeObjectURL(s.dataUrl));
    setShots([]);
    onClose?.();
  };

  // Group shots by category for the multi-batch upload (one POST per category).
  const uploadAll = async () => {
    if (!shots.length || !caseId) return;
    setUploading(true);
    try {
      const byCat = shots.reduce((acc, s) => {
        const k = s.category || "schadenstelle";
        (acc[k] = acc[k] || []).push(s);
        return acc;
      }, {});
      const allUploaded = [];
      for (const [cat, list] of Object.entries(byCat)) {
        const fd = new FormData();
        const blobs = await Promise.all(
          list.map((s) => compressImage(new File([s.blob], "capture.jpg", { type: "image/jpeg" })))
        );
        blobs.forEach((b, i) => fd.append("files", b, `capture_${Date.now()}_${i}.jpg`));
        fd.append("category", cat);
        const { data } = await api.post(uploadPath || `/files/cases/${caseId}/photos`, fd, {
        });
        allUploaded.push(...(data.photos || []));
      }
      onUploaded?.(allUploaded);
      toast.success(`${allUploaded.length} Foto${allUploaded.length === 1 ? "" : "s"} hochgeladen`);
      close();
    } catch (e) {
      toast.error(apiError(e, "Upload fehlgeschlagen"));
    } finally {
      setUploading(false);
    }
  };

  // Per-category counts for chips + progress dots
  const counts = shots.reduce((a, s) => { a[s.category] = (a[s.category] || 0) + 1; return a; }, {});

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black z-50 flex flex-col" data-testid="camera-capture-modal">
      {/* ── Header: X | Titel + Beschreibung | 👁 Overlay-Toggle ── */}
      <div className="shrink-0 flex items-center gap-2 px-3 py-2.5 bg-black text-white z-30">
        <button
          onClick={close}
          className="h-10 w-10 rounded-full bg-white/12 hover:bg-white/25 flex items-center justify-center shrink-0"
          data-testid="camera-close"
          aria-label="Schließen"
        >
          <X className="h-5 w-5" />
        </button>
        <div className="flex-1 min-w-0 text-center">
          <div className="font-display font-bold text-[15px] leading-tight truncate">
            {activeMeta?.title || "Foto-Serie"}
          </div>
          <div className="text-[11px] text-white/60 leading-tight truncate">
            {activeMeta?.desc || "Kategorie wählen"}
          </div>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          {torchSupported && (
            <button
              onClick={toggleTorch}
              className={`h-10 w-10 rounded-full flex items-center justify-center transition ${
                torchOn ? "bg-yellow-400 text-black" : "bg-white/12 text-white hover:bg-white/25"
              }`}
              data-testid="camera-torch-toggle"
              aria-label="Taschenlampe umschalten"
              title="Taschenlampe für dunkle Umgebungen"
            >
              {torchOn ? <Flashlight className="h-5 w-5" /> : <FlashlightOff className="h-5 w-5" />}
            </button>
          )}
          <button
            onClick={flip}
            className="h-10 w-10 rounded-full bg-white/12 hover:bg-white/25 text-white flex items-center justify-center"
            data-testid="camera-flip"
            aria-label="Kamera umschalten"
          >
            <RotateCcw className="h-4 w-4" />
          </button>
          <button
            onClick={() => setShowOverlay((v) => !v)}
            className={`h-10 w-10 rounded-full flex items-center justify-center transition ${
              showOverlay ? "bg-sky-500 text-white" : "bg-white/12 text-white hover:bg-white/25"
            }`}
            data-testid="camera-overlay-toggle"
            aria-label="Beispiel-Overlay umschalten"
            title="Beispiel über dem Bild ein-/ausblenden"
          >
            <Eye className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* ── Kategorie-Chips (aktive blau) ── */}
      <div ref={chipsRef} className="shrink-0 bg-black overflow-x-auto px-2 pb-2 scrollbar-thin z-30" data-testid="camera-category-chips">
        <div className="flex items-center gap-1.5 min-w-min">
          {categories.map((cat) => {
            const active = activeCategory === cat.key;
            const cnt = counts[cat.key] || 0;
            return (
              <button
                key={cat.key}
                type="button"
                data-active={active ? "true" : "false"}
                onClick={() => setActiveCategory(cat.key)}
                className={`shrink-0 px-3.5 h-8 rounded-full text-[12px] font-semibold whitespace-nowrap transition flex items-center gap-1.5 ${
                  active ? "bg-sky-500 text-white" : "bg-white/12 text-white/85 hover:bg-white/20"
                }`}
                data-testid={`camera-cat-${cat.key}`}
              >
                {cat.title}
                {cnt > 0 && (
                  <span className={`h-4 min-w-4 px-1 rounded-full text-[9px] font-bold flex items-center justify-center ${active ? "bg-white text-sky-600" : "bg-emerald-500 text-white"}`}>
                    {cnt}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* ── Sucher ── */}
      <div
        className="flex-1 relative bg-black overflow-hidden"
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        <video
          ref={videoRef}
          playsInline
          muted
          autoPlay
          className="absolute inset-0 w-full h-full object-contain"
          style={!hasHwZoom && zoom > 1 ? { transform: `scale(${zoom})` } : undefined}
          data-testid="camera-video"
        />
        <canvas ref={canvasRef} className="hidden" />

        {/* „Wischen für nächstes Bild" — Pfeile + Swipe (wie native App) */}
        {categories.length > 1 && (
          <div className="absolute top-2.5 left-1/2 -translate-x-1/2 z-20 flex items-center gap-0.5 bg-black/55 backdrop-blur rounded-full px-1 py-1 text-white" data-testid="camera-swipe-pill">
            <button
              onClick={() => gotoCategory(activeIdx - 1)}
              className="h-7 w-7 rounded-full hover:bg-white/15 flex items-center justify-center"
              aria-label="Vorherige Kategorie"
              data-testid="camera-cat-prev"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span className="text-[11px] font-semibold px-1 select-none">Wischen für nächstes Bild</span>
            <button
              onClick={() => gotoCategory(activeIdx + 1)}
              className="h-7 w-7 rounded-full hover:bg-white/15 flex items-center justify-center"
              aria-label="Nächste Kategorie"
              data-testid="camera-cat-next"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        )}

        {/* Ghost-Overlay: Beispielbild halbtransparent über dem Live-Bild */}
        {showOverlay && activeMeta && (
          <div className="absolute inset-0 pointer-events-none flex items-center justify-center" data-testid="camera-overlay">
            <img
              src={`/photo-examples/${activeMeta.key}.png`}
              alt=""
              className="max-w-[92%] max-h-[80%] object-contain opacity-40"
              draggable={false}
              onError={(e) => { e.currentTarget.style.display = "none"; }}
            />
          </div>
        )}

        {/* Capture flash */}
        {flash && <div className="absolute inset-0 bg-white/50 transition-opacity" />}

        {/* Zoom-Steuerung (wie native Kamera): 1×/2×-Presets + Pinch-Anzeige */}
        {ready && (
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 z-20 flex items-center gap-1.5" data-testid="camera-zoom-controls">
            {zoomBadge && (
              <span className="text-[11px] font-bold text-yellow-300 bg-black/60 backdrop-blur rounded-full px-2 py-1 select-none" data-testid="camera-zoom-badge">
                {zoom.toFixed(1).replace(".", ",")}×
              </span>
            )}
            <div className="flex items-center gap-1 bg-black/55 backdrop-blur rounded-full p-1">
              {[1, 2].map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => applyZoom(p)}
                  className={`h-8 min-w-8 px-1.5 rounded-full text-[11px] font-bold transition ${
                    Math.abs(zoom - p) < 0.25
                      ? "bg-white text-black"
                      : "text-white/85 hover:bg-white/15"
                  }`}
                  data-testid={`camera-zoom-${p}x`}
                  aria-label={`${p}× Zoom`}
                >
                  {p}×
                </button>
              ))}
            </div>
          </div>
        )}

        {!ready && !error && (
          <div className="absolute inset-0 flex items-center justify-center text-white/80">
            <div className="flex flex-col items-center gap-2">
              <Loader2 className="h-8 w-8 animate-spin" />
              <div className="text-sm">Kamera wird geöffnet…</div>
            </div>
          </div>
        )}
        {error && (
          <div className="absolute inset-0 flex items-center justify-center text-white p-8 text-center">
            <div className="max-w-sm space-y-2">
              <div className="font-display text-xl font-bold">Kamera nicht verfügbar</div>
              <div className="text-sm text-white/80">{error}</div>
              <div className="text-xs text-white/60 mt-4">Bitte erlauben Sie Kamera-Zugriff in den Browser-Einstellungen.</div>
            </div>
          </div>
        )}

        {/* Captured-shots strip (rechts) */}
        {shots.length > 0 && (
          <div className="absolute top-12 right-2 max-h-[40vh] overflow-y-auto flex flex-col gap-1.5 z-20" data-testid="camera-shots-strip">
            {shots.slice(-6).map((s, idx) => {
              const realIdx = shots.length - 6 + idx >= 0 ? shots.length - 6 + idx : idx;
              return (
                <div key={realIdx} className="relative h-14 w-14 rounded-lg overflow-hidden border-2 border-white/40 shadow-lg">
                  <img src={s.dataUrl} alt="" className="w-full h-full object-cover" />
                  <button
                    type="button"
                    onClick={() => removeShot(realIdx)}
                    className="absolute top-0.5 right-0.5 h-4 w-4 rounded-full bg-black/70 hover:bg-rose-600 text-white flex items-center justify-center"
                    aria-label="Entfernen"
                    data-testid={`camera-shot-del-${realIdx}`}
                  >
                    <X className="h-2.5 w-2.5" />
                  </button>
                </div>
              );
            })}
            {shots.length > 6 && (
              <div className="text-[10px] text-white/70 font-bold text-center">+{shots.length - 6}</div>
            )}
          </div>
        )}
      </div>

      {/* ── Fortschritts-Punkte pro Kategorie ── */}
      {categories.length > 1 && (
        <div className="shrink-0 bg-black flex items-center justify-center gap-1.5 py-2 px-4 flex-wrap z-30" data-testid="camera-progress-dots">
          {categories.map((cat, i) => {
            const active = i === activeIdx;
            const done = (counts[cat.key] || 0) > 0;
            return (
              <button
                key={cat.key}
                onClick={() => setActiveCategory(cat.key)}
                aria-label={cat.title}
                className={`rounded-full transition-all ${
                  active ? "w-5 h-1.5 bg-white" : done ? "w-1.5 h-1.5 bg-emerald-400" : "w-1.5 h-1.5 bg-white/30"
                }`}
              />
            );
          })}
        </div>
      )}

      {/* ── Bottom-Bar: Galerie | Auslöser | Hochladen ── */}
      <div className="shrink-0 bg-black text-white px-5 pb-[max(1rem,env(safe-area-inset-bottom))] pt-1.5 flex items-center justify-between">
        <button
          type="button"
          onClick={() => galleryInputRef.current?.click()}
          className="flex flex-col items-center gap-1 text-white/85 hover:text-white w-16"
          data-testid="camera-gallery-upload"
        >
          <span className="h-10 w-10 rounded-xl bg-white/12 flex items-center justify-center">
            <ImageIcon className="h-5 w-5" />
          </span>
          <span className="text-[10px] font-semibold">Galerie</span>
        </button>
        <input
          ref={galleryInputRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={(e) => { onGalleryFiles(e.target.files); e.target.value = ""; }}
          data-testid="camera-gallery-input"
        />

        <button
          type="button"
          onClick={capture}
          disabled={!ready || !activeCategory}
          className="relative h-[68px] w-[68px] rounded-full bg-white shadow-xl flex items-center justify-center disabled:opacity-40 active:scale-95 transition-transform"
          data-testid="camera-shutter"
          aria-label="Foto aufnehmen"
        >
          <span className="h-[54px] w-[54px] rounded-full ring-4 ring-zinc-900 bg-white" />
        </button>

        <button
          type="button"
          onClick={uploadAll}
          disabled={uploading || shots.length === 0 || !caseId}
          className="flex flex-col items-center gap-1 text-white/85 hover:text-white disabled:opacity-40 w-16"
          data-testid="camera-upload"
        >
          <span className={`h-10 w-10 rounded-xl flex items-center justify-center ${shots.length > 0 ? "brand-gradient-bg" : "bg-white/12"}`}>
            {uploading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Check className="h-5 w-5" />}
          </span>
          <span className="text-[10px] font-semibold">
            {shots.length > 0 ? `Hochladen (${shots.length})` : "Fertig"}
          </span>
        </button>
      </div>
    </div>
  );
}
